import type { PaymentMethods, ReceiverTypes } from 'api-graphql-types/enums'
import type { PostHogConfig } from 'posthog-js'

type AnalyticsFn<Data extends {}> = (data: Data) => void

export type ReceiverIdentification = {
  id: string
  receiver_type: ReceiverType | ReceiverTypes
}

export type PPPClientBannerData = { client_name: string }

export type PPPMatchingEventData = {
  id: string
  event_type?: 'matching_event' | 'verdopplungsaktion' | 'spendenaktion'
  name: string
  actor: string
}

export type ReceiverPageData = { id: string; page_type: 'fundraising_event' | 'project'; title: string }

export type DonationData = {
  donation_amount: number
  recurring_donation: string
  payment_method: string
  codonation_amount: number
  show_amount_selected: boolean
  show_name_selected: boolean
  donation_receipt_requested: boolean
  platform_newsletter_selected: boolean
  comment_added: boolean
}
export type DonationFormData = ReceiverIdentification & {
  donation_form_type: DonationFormType
  available_payment_methods?: PaymentMethods[]
  data_imported?: boolean
}

export type CodonationChangeData = DonationFormData & {
  codonation_amount: number
  donation_amount: number
  no_of_increases: number
  no_of_decreases: number
  bucket_index: number | null
  percent_of_bucket_bracket: number | null
  percent_of_max: number | null
  max: number
  step: number
}

export type DonationCompletedData = DonationData &
  DonationFormData & { postdonation_redirect_url: string; donation_id: string }

export type DonationSubmittedData = DonationData & DonationFormData

export type DonationErrorMessageDisplayed = DonationFormData & {
  error: string
  errors: object
}

export type DonationButtonClickedData = ReceiverIdentification & {
  button_location: DonateButtonLocation
}

export type DonationAmountChangedData = DonationFormData & {
  previous_donation_amount: number
  new_donation_amount: number
  selection_type: 'donation_amount_button' | 'donation_own_amount'
}

export type DonationPaginationClickedData = ReceiverPageData & {
  pagination_direction: 'forward' | 'back'
}

export type ShareButtonClickedData = ReceiverPageData & {
  button_location: 'fun_e_main' | 'fun_e_sticky' | 'fun_e_admin_box' | 'ppp_main' | 'ppp_sticky'
}

export type SharingLocation = 'sharing_modal' | 'social_sharing_panel'

export type SocialSharingClickedData = ReceiverPageData & {
  icon_location: SharingLocation
  sharing_option: SharingOption
}

export type StreambotUrlCopiedData = {
  url: string
  fundraising_event_id: string
  location: 'overview' | 'widget'
}

export type StreambotSettingsModifiedData = {
  fundraising_event_id: string
  widget: string
}

export type PaymentMethodChangedData = ReceiverIdentification & {
  donation_form_type: DonationFormType
  previous_payment_method: PaymentMethods
  new_payment_method: PaymentMethods
  id: string
}

export type UnsubscribeLinkClicked = {
  id: string
  donation_form_type: DonationFormType
}

export type DirectDepositIbanCopiedData = {
  id: string
  donation_form_type: DonationFormType
}
export type DonationListScrolledData = {
  id: string
  page_type: 'fundraising_event' | 'project'
  title: string
  number_of_scrolls: number
  time_spent_scrolling: number
}

export type Config = {
  apiKey: string
  enable: boolean
  debug?: boolean
  bootstrapData?: PostHogConfig['bootstrap']
}

export type DonateButtonSafariPaypalLoadingErrorData = {
  navigator: string
  os: string
}

export type ReceiverType = 'Project' | 'FundraisingEvent'

export type DonateButtonPPPLocation = 'PPP Main' | 'PPP Sticky' | 'PPP MatchingEvent' | `PPP Need ${number}`
export type DonateButtonFunELocation = 'FunE Main' | 'FunE Share' | 'FunE Sticky'

export type SharingOption = 'facebook' | 'whatsapp' | 'messenger' | 'link_copy' | 'instagram' | 'email'

export type DonateButtonLocation = DonateButtonPPPLocation | DonateButtonFunELocation
export type CleanupFunction = () => void
export type FeatureFlagValue = string | boolean | undefined
export type FeatureFlagCallback = (value: FeatureFlagValue) => void
export interface Analytics {
  initialize(cfg: Config): void
  isInitialized(): boolean
  getDistinctId(): string
  optOut(): void
  optIn(): void
  optInOutNotSet(): void
  optInOutPending(): void
  reset(): void
  getFeatureFlag(name: FeatureFlagKey): FeatureFlagValue
  listenToFeatureFlag(name: FeatureFlagKey, callback: FeatureFlagCallback): CleanupFunction
  donationButtonClicked: AnalyticsFn<DonationButtonClickedData>
  donationFormOpened: AnalyticsFn<DonationFormData>
  donationPaginationClicked: AnalyticsFn<DonationPaginationClickedData>
  shareButtonClicked: AnalyticsFn<ShareButtonClickedData>
  socialSharingClicked: AnalyticsFn<SocialSharingClickedData>
  coDonationModalOpened: AnalyticsFn<DonationFormData>
  donationSubmitted: AnalyticsFn<DonationSubmittedData>
  donationCompleted: AnalyticsFn<DonationCompletedData>
  donationSafariPaypalLoadingError: AnalyticsFn<DonateButtonSafariPaypalLoadingErrorData>
  donationErrorMessageDisplayed: AnalyticsFn<DonationErrorMessageDisplayed>
  coDonationSetToZero: AnalyticsFn<DonationFormData>
  coDonationChange: AnalyticsFn<CodonationChangeData>
  streambotUrlCopied: AnalyticsFn<StreambotUrlCopiedData>
  streambotSettingsModified: AnalyticsFn<StreambotSettingsModifiedData>
}

export const FeatureFlagKey = {
  ExpressCheckout: 'express-checkout',
  TeamFeature: 'team-feature',
  FeatureFlagMismatch: 'feature-flag-mismatch',
} as const
export type FeatureFlagKey = (typeof FeatureFlagKey)[keyof typeof FeatureFlagKey]
export type FeatureFlagData = Partial<Record<FeatureFlagKey, string | boolean>>

export type PosthogBootstrapDataCookieValue = {
  distinctID: string
  featureFlags: FeatureFlagData
}

export const DonationFormType = {
  Project: 'Project',
  FundraisingEvent: 'FundraisingEvent',
  WirWunderWithoutCodonation: 'WirWunder without CD',
  WirWunderWithCodonation: 'WirWunder with CD',
  OtherClients: 'OtherClients',
  Iframe: 'iframe',
} as const

export type DonationFormType = (typeof DonationFormType)[keyof typeof DonationFormType]
